import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Download, X } from "lucide-react";
import { http, errMsg, saveBlob } from "../../lib/api";
import { Btn } from "../kit/Kit";

const ext = (n) => (n.split(".").pop() || "").toLowerCase();
const IMG = ["jpg", "jpeg", "png", "gif", "webp", "bmp"];
const itemSrc = (id) => ({ archivo: `/carga/items/${encodeURIComponent(id)}/archivo`, texto: `/carga/items/${encodeURIComponent(id)}/texto` });

const Pane = ({ nombre, src, titulo, onBlob }) => {
  const [url, setUrl] = useState(null);
  const [txt, setTxt] = useState(null);
  const [err, setErr] = useState(null);
  const e = ext(nombre);
  const visual = e === "pdf" || IMG.includes(e);
  useEffect(() => {
    let u;
    const o = { params: src.params, timeout: 120000 };
    (visual ? http.get(src.archivo, { ...o, responseType: "blob" }).then((r) => { onBlob?.(r.data); u = URL.createObjectURL(r.data); setUrl(u); })
      : http.get(src.texto, o).then((r) => setTxt(r.data.texto || "Sin texto extraíble para este formato."))).catch((x) => setErr(errMsg(x)));
    return () => u && URL.revokeObjectURL(u);
  }, [src.archivo, src.texto, visual]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {titulo && <p className="border-b border-[#EFEBE4] bg-white px-4 py-1.5 text-[11px] font-semibold text-[#5C5850]">{titulo}</p>}
      <div className="min-h-0 flex-1 overflow-auto bg-[#FAF8F5]">
        {err && <p className="p-6 text-sm text-[#B42318]" data-testid="carga-vista-error">{err}</p>}
        {!err && !url && txt === null && <p className="p-6 text-sm text-[#8C867A]">Cargando…</p>}
        {url && e === "pdf" && <iframe src={url} title={nombre} className="h-full w-full" data-testid="carga-vista-pdf" />}
        {url && e !== "pdf" && <img src={url} alt={nombre} className="mx-auto max-h-full max-w-full object-contain p-4" data-testid="carga-vista-img" />}
        {txt !== null && <pre className="whitespace-pre-wrap p-6 font-mono text-xs leading-relaxed" data-testid="carga-vista-texto">{txt}</pre>}
      </div>
    </div>
  );
};

// Visor único (bandeja y expedientes): PDF en iframe, imágenes en línea, resto como texto extraído; duplicados lado a lado con su original.
export const Vista = ({ it, cerrar, src, extra }) => {
  const s = src || itemSrc(it.id);
  const [blob, setBlob] = useState(null);
  const orig = it.duplicado_de?.item;
  const par = it.par;
  const bajar = async () => {
    try { saveBlob(blob || (await http.get(s.archivo, { params: s.params, responseType: "blob", timeout: 120000 })).data, it.nombre); } catch (x) { /* el panel muestra el error */ }
  };
  return createPortal(
    <div className="fixed inset-0 z-[60] flex items-end justify-center bg-black/50 backdrop-blur-sm sm:items-center sm:p-6" data-testid="carga-vista">
      <div className={`flex h-[92vh] w-full ${orig || par ? "max-w-7xl" : "max-w-5xl"} flex-col rounded-t-2xl bg-white shadow-xl sm:rounded-2xl`}>
        <div className="flex items-center justify-between gap-3 border-b border-[#EFEBE4] px-5 py-3">
          <p className="truncate text-sm font-semibold" title={it.ruta}>{it.nombre}</p>
          <div className="flex items-center gap-2"><Btn kind="secondary" onClick={bajar} data-testid="carga-vista-descargar"><Download className="h-4 w-4" />Descargar</Btn>
            <button onClick={cerrar} data-testid="carga-vista-cerrar"><X className="h-5 w-5" /></button></div>
        </div>
        <div className="flex min-h-0 flex-1 divide-x divide-[#EFEBE4]">
          <Pane nombre={it.nombre} src={s} onBlob={setBlob} titulo={orig ? "Este documento" : par ? "En plataforma" : null} />
          {par && <Pane nombre={par.nombre} src={par.src} titulo={par.titulo} />}
          {orig && <Pane nombre={it.duplicado_de.nombre || it.nombre} src={itemSrc(orig)} titulo={`Copia existente · ${it.duplicado_de.donde}: ${it.duplicado_de.nombre}`} />}
        </div>
        {extra && <div className="max-h-[35vh] overflow-auto border-t border-[#EFEBE4] p-4">{extra}</div>}
      </div>
    </div>,
    document.body,
  );
};
